import { GridRowGroups } from "../grid/GroupHeadings";
import { Fragment } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { formatCopy } from "../../theme/copy";
import { Box } from "@mui/material";
import { DiscreteScaleSlider, DiscreteScaleStars } from "../../components/ui";
import ActionButton from "../../components/ui/ActionButton";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import { GridColumnHeaderText } from "../grid/GridColumnHeaderText";
import { GridRowContent } from "../grid/GridRowContent";
import {
  compactGridButtonStyle,
  compactGridCardStyle,
  compactGridListStyle,
  optionalGridLineStyle,
  optionalGridScaleInsetStyle,
  wideGridButtonStyle,
  wideGridCellStyle,
  wideGridColumnHeaderStyle,
  wideGridInstrumentStyle,
  wideGridRowHeaderStyle,
  wideGridTableStyle,
  wideGridViewportStyle,
} from "../grid/gridInstrumentStyle";
import { portraitRowHeader } from "../grid/portraitStyles";
import { ratingButtonDescription } from "../rating/labels";
import type {
  GridRatingDefinition,
  GridRatingDraft,
  GridRatingEvent,
} from "./core";

type GridRatingViewProps = QuestionViewProps<
  GridRatingDefinition,
  GridRatingDraft,
  GridRatingEvent
>;

const GridRatingView = (props: GridRatingViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const { definition, draft } = props;
  const compact = props.display.arrangement === "compact";
  const hasRowGroups = Boolean(definition.rowGroups?.length);
  const grouped = hasRowGroups;
  const showGridLines = (theme.show_grid_lines || hasRowGroups) && !compact;
  const mode = props.display.view;
  const headerLabel = (option: GridRatingDefinition["options"][number]) =>
    mode === "buttons"
      ? ratingButtonDescription(option)
      : option.displayLabel
        ? option.label
        : "";
  const isDiscreteScale = mode === "slider" || mode === "star";
  const showHeaders =
    isDiscreteScale ||
    definition.options.some((option) => headerLabel(option).trim());
  const labelledOptionIndexes = definition.options.flatMap((option, index) =>
    option.displayLabel && option.label ? [index] : [],
  );
  const scaleHeaderWidth = (index: number) => {
    const labelIndex = labelledOptionIndexes.indexOf(index);
    if (labelIndex === -1) return undefined;
    const lastIndex = definition.options.length - 1;
    if (lastIndex === 0) return "100%";
    const previous = labelledOptionIndexes[labelIndex - 1];
    const next = labelledOptionIndexes[labelIndex + 1];
    const leftSpace = previous === undefined ? index : (index - previous) / 2;
    const rightSpace =
      next === undefined ? lastIndex - index : (next - index) / 2;
    const width =
      index === 0
        ? rightSpace
        : index === lastIndex
          ? leftSpace
          : Math.min(leftSpace, rightSpace) * 2;
    return `${width * 100}%`;
  };
  const scaleHeader = (option: GridRatingDefinition["options"][number]) => (
    <>
      <Box>
        {headerLabel(option) && (
          <GridColumnHeaderText text={headerLabel(option)} />
        )}
      </Box>
      {isDiscreteScale && (
        <Box
          sx={{
            color: theme.tokens.color.action.primary,
            fontWeight: theme.tokens.typography.weight.medium,
          }}
        >
          {option.value}
        </Box>
      )}
    </>
  );

  const scaleTrackHeaders = (small = false) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.max(definition.options.length - 1, 1)}, minmax(0, 1fr))`,
        fontSize: small ? "10px" : undefined,
        lineHeight: small ? 1.2 : undefined,
        paddingBlock: `${theme.tokens.spacing["2"]}px`,
        ...optionalGridScaleInsetStyle(theme, small, showGridLines),
      }}
    >
      {definition.options.map((option, index) => {
        const last = index === definition.options.length - 1;
        return (
          <Box
            key={option.value}
            data-grid-rating-scale-header={option.value}
            sx={{
              gridRow: 1,
              gridColumn: last
                ? `${Math.max(definition.options.length - 1, 1)}`
                : `${index + 1}`,
              justifySelf: index === 0 ? "start" : last ? "end" : "start",
              minWidth: 0,
              width: scaleHeaderWidth(index),
              maxWidth:
                option.displayLabel && option.label ? undefined : "100%",
              color: small
                ? theme.tokens.color.text.primary
                : theme.tokens.color.action.primary,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflowWrap: "anywhere",
              textAlign: index === 0 ? "left" : last ? "right" : "center",
              transform: index > 0 && !last ? "translateX(-50%)" : undefined,
            }}
          >
            {scaleHeader(option)}
          </Box>
        );
      })}
    </Box>
  );

  const rowContent = (row: GridRatingDefinition["rows"][number]) => (
    <GridRowContent
      row={row}
      compact={compact}
      specify={props.controls.specify}
    />
  );

  const scale = (
    row: GridRatingDefinition["rows"][number],
    rowIndex: number,
  ) => {
    const selected = draft.selectedByRow[row.id];
    if (mode === "buttons") {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${definition.options.length}, minmax(0, 1fr))`,
            gap: `${theme.tokens.spacing["1"]}px`,
          }}
        >
          {definition.options.map((option, optionIndex) => (
            <ActionButton
              key={option.value}
              testId={`chip-${rowIndex + 1}-${optionIndex + 1}`}
              ariaLabel={`${draft.specifiedRows?.[row.id] || row.label}: ${option.value}${ratingButtonDescription(option) ? ` ${option.label}` : ""}`}
              ariaPressed={selected === option.value}
              isSelected={selected === option.value}
              onClick={() =>
                props.dispatch({
                  type: "select",
                  rowId: row.id,
                  value: option.value,
                })
              }
              style={compactGridButtonStyle(theme, selected === option.value)}
            >
              {option.value}
            </ActionButton>
          ))}
        </Box>
      );
    }
    if (mode === "star") {
      return (
        <DiscreteScaleStars
          testId={`grid-rating-stars-${rowIndex + 1}`}
          name={`grid-rating-${rowIndex}`}
          ariaLabel={formatCopy(theme.copy.labels.rowRating, {
            row: draft.specifiedRows?.[row.id] || row.label,
          })}
          points={definition.options}
          selectedValue={selected}
          spread
          showDividers={showGridLines}
          onSelect={(value) =>
            props.dispatch({ type: "select", rowId: row.id, value })
          }
        />
      );
    }
    return (
      <DiscreteScaleSlider
        testId={`slider-${rowIndex}`}
        ariaLabel={formatCopy(theme.copy.labels.rowRatingScale, {
          row: draft.specifiedRows?.[row.id] || row.label,
        })}
        points={definition.options}
        selectedValue={selected}
        onSelect={(value) =>
          props.dispatch({ type: "select", rowId: row.id, value })
        }
      />
    );
  };

  const rowControls = (
    row: GridRatingDefinition["rows"][number],
    index: number,
  ) => (
    <Box
      key={row.id}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>{scale(row, index)}</Box>
      {definition.dontKnow && (
        <Box sx={{ alignSelf: "flex-end" }}>
          <ActionButton
            currentVal={{ text: definition.dontKnow.label, display_text: true }}
            isSelected={
              draft.selectedByRow[row.id] === definition.dontKnow.value
            }
            ariaDisabled={!props.controls.canSubmit}
            onClick={() => props.dispatch({ type: "dont-know", rowId: row.id })}
            testId={`grid-rating-dont-know-${index}`}
          />
        </Box>
      )}
    </Box>
  );

  const wideButton = (
    row: GridRatingDefinition["rows"][number],
    rowIndex: number,
    option: GridRatingDefinition["options"][number],
    optionIndex: number,
  ) => (
    <ActionButton
      testId={`chip-${rowIndex + 1}-${optionIndex + 1}`}
      ariaLabel={`${draft.specifiedRows?.[row.id] || row.label}: ${option.value}${ratingButtonDescription(option) ? ` ${option.label}` : ""}`}
      ariaPressed={draft.selectedByRow[row.id] === option.value}
      isSelected={draft.selectedByRow[row.id] === option.value}
      onClick={() =>
        props.dispatch({ type: "select", rowId: row.id, value: option.value })
      }
      style={wideGridButtonStyle(
        theme,
        draft.selectedByRow[row.id] === option.value,
      )}
    >
      {option.value}
    </ActionButton>
  );

  return (
    <Box
      data-testid="grid-rating-view"
      data-grid-ready="true"
      sx={{
        width: "100%",
        ...(compact
          ? {}
          : wideGridInstrumentStyle(theme, theme.show_grid_lines, grouped)),
      }}
    >
      {compact ? (
        <Box sx={compactGridListStyle(theme)}>
          {definition.rows.map((row, index) => (
            <Fragment key={row.id}>
              <VisualGroupHeading
                text={groupHeading(definition.rowGroups, row.id)}
              />
              <Box
                sx={{
                  ...compactGridCardStyle(theme),
                  paddingInline: `${theme.tokens.spacing["6"]}px`,
                }}
              >
                <Box sx={portraitRowHeader}>{rowContent(row)}</Box>
                {mode === "slider" ? (
                  scaleTrackHeaders(true)
                ) : showHeaders ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${definition.options.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {definition.options.map((option) => (
                      <Box
                        key={option.value}
                        data-grid-rating-scale-header={option.value}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          minWidth: 0,
                          height: "100%",
                          color: theme.tokens.color.text.primary,
                          fontSize: "10px",
                          lineHeight: 1.2,
                          overflowWrap: "anywhere",
                          textAlign: "center",
                        }}
                      >
                        {scaleHeader(option)}
                      </Box>
                    ))}
                  </Box>
                ) : null}
                {rowControls(row, index)}
              </Box>
            </Fragment>
          ))}
        </Box>
      ) : (
        <Box sx={wideGridViewportStyle}>
          <Box component="table" sx={wideGridTableStyle}>
            <colgroup>
              <col style={{ width: "15%" }} />
            </colgroup>
            <colgroup>
              {definition.options.map((option) => (
                <col key={option.value} />
              ))}
            </colgroup>
            {definition.dontKnow && (
              <colgroup>
                <col style={{ width: "22%" }} />
              </colgroup>
            )}
            {showHeaders && (
              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    scope="col"
                    sx={wideGridRowHeaderStyle(theme)}
                  />
                  {mode === "slider" ? (
                    <Box
                      component="th"
                      scope="colgroup"
                      colSpan={definition.options.length}
                      sx={{
                        padding: 0,
                        ...optionalGridLineStyle(theme),
                      }}
                    >
                      {scaleTrackHeaders()}
                    </Box>
                  ) : (
                    definition.options.map((option) => (
                      <Box
                        component="th"
                        scope="col"
                        key={option.value}
                        data-grid-rating-scale-header={option.value}
                        sx={wideGridColumnHeaderStyle(theme)}
                      >
                        {mode === "star"
                          ? scaleHeader(option)
                          : headerLabel(option) && (
                              <GridColumnHeaderText
                                text={headerLabel(option)}
                              />
                            )}
                      </Box>
                    ))
                  )}
                  {definition.dontKnow && (
                    <Box
                      component="th"
                      scope="col"
                      sx={wideGridColumnHeaderStyle(theme)}
                    />
                  )}
                </Box>
              </Box>
            )}
            <GridRowGroups
              rows={definition.rows}
              groups={definition.rowGroups}
              rowId={(row) => row.id}
              columns={
                1 +
                definition.options.length +
                Number(Boolean(definition.dontKnow))
              }
            >
              {(row, rowIndex) => (
                <Box component="tr">
                  <Box
                    component="th"
                    scope="row"
                    sx={wideGridRowHeaderStyle(theme)}
                  >
                    {rowContent(row)}
                  </Box>
                  {mode === "buttons" ? (
                    definition.options.map((option, optionIndex) => (
                      <Box
                        component="td"
                        key={option.value}
                        sx={wideGridCellStyle(theme)}
                      >
                        {wideButton(row, rowIndex, option, optionIndex)}
                      </Box>
                    ))
                  ) : (
                    <Box
                      component="td"
                      colSpan={definition.options.length}
                      sx={{
                        verticalAlign: "middle",
                        ...optionalGridLineStyle(theme),
                        ...(mode === "slider"
                          ? optionalGridScaleInsetStyle(
                              theme,
                              false,
                              showGridLines,
                            )
                          : showGridLines
                            ? { padding: 0 }
                            : {}),
                      }}
                    >
                      {scale(row, rowIndex)}
                    </Box>
                  )}
                  {definition.dontKnow && (
                    <Box
                      component="td"
                      sx={{
                        textAlign: "center",
                        ...optionalGridLineStyle(theme),
                      }}
                    >
                      <ActionButton
                        currentVal={{
                          text: definition.dontKnow.label,
                          display_text: true,
                        }}
                        isSelected={
                          draft.selectedByRow[row.id] ===
                          definition.dontKnow.value
                        }
                        ariaDisabled={!props.controls.canSubmit}
                        onClick={() =>
                          props.dispatch({ type: "dont-know", rowId: row.id })
                        }
                        testId={`grid-rating-dont-know-${rowIndex}`}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </GridRowGroups>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const GridRatingButtonsView = GridRatingView;
export const GridRatingStarsView = GridRatingView;
export const GridRatingSliderView = GridRatingView;
